import type { StoryDirection } from '@rdd/contracts'
import type { PublicPlayer } from '../net/protocol'
import { STEP_MS } from './walker'

/**
 * Os OUTROS jogadores na tela (modo_historia/04#HM-001). Logica pura: o estado chega do servidor em saltos de
 * um tile, e aqui ele vira deslizamento -- cada mudanca de tile anima de onde o avatar ESTA ate o tile novo, no
 * mesmo tempo de um passo.
 */

export type RemoteView = {
  id: string
  nick: string
  /** O personagem que o avatar veste, ou `null` para o sprite padrao (docs/21 secao 6). */
  character: string | null
  x: number
  y: number
  hop: number
  facing: StoryDirection
  dueling: boolean
}

type Entry = {
  player: PublicPlayer
  fromX: number
  fromY: number
  elapsed: number
}

export class RemotePlayers {
  private readonly entries = new Map<string, Entry>()

  constructor(private readonly self: string) {}

  /** Aplica a foto inteira: quem nao esta nela, saiu. */
  reset(players: readonly PublicPlayer[]): void {
    this.entries.clear()
    this.upsert(players)
  }

  upsert(players: readonly PublicPlayer[]): void {
    for (const player of players) {
      if (player.id === this.self) continue
      const entry = this.entries.get(player.id)
      if (entry === undefined) {
        this.entries.set(player.id, { player, fromX: player.x, fromY: player.y, elapsed: STEP_MS })
        continue
      }
      const moved = entry.player.x !== player.x || entry.player.y !== player.y
      if (moved) {
        const now = this.positionOf(entry)
        entry.fromX = now.x
        entry.fromY = now.y
        entry.elapsed = 0
      }
      entry.player = player
    }
  }

  remove(ids: readonly string[]): void {
    for (const id of ids) this.entries.delete(id)
  }

  advance(deltaMs: number): void {
    for (const entry of this.entries.values()) entry.elapsed = Math.min(STEP_MS, entry.elapsed + deltaMs)
  }

  views(): RemoteView[] {
    return [...this.entries.values()].map((entry) => {
      const position = this.positionOf(entry)
      const t = entry.elapsed / STEP_MS
      return {
        id: entry.player.id,
        nick: entry.player.nick,
        character: entry.player.character,
        x: position.x,
        y: position.y,
        hop: t < 1 ? Math.sin(Math.PI * t) : 0,
        facing: entry.player.dir,
        dueling: entry.player.status === 'dueling',
      }
    })
  }

  private positionOf(entry: Entry): { x: number; y: number } {
    const t = Math.min(1, entry.elapsed / STEP_MS)
    return {
      x: entry.fromX + (entry.player.x - entry.fromX) * t,
      y: entry.fromY + (entry.player.y - entry.fromY) * t,
    }
  }
}
